// CRM Suppressions — rehouses the classic Suppressions screen into the Deliverability shell.
// The suppression list is the send-path gate: no email goes to an address on it. This screen
// manages it — add single, bulk-add (paste), filter by reason, search, paginate, delete.
// Client-scoped, self-contained.
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';

const PAGE = 100;
const REASONS = [
  { value: 'manual', label: 'Manual', cls: 'crmsp-r-slate' },
  { value: 'unsubscribed', label: 'Unsubscribed', cls: 'crmsp-r-amber' },
  { value: 'bounced', label: 'Bounced', cls: 'crmsp-r-rose' },
  { value: 'complaint', label: 'Complaint', cls: 'crmsp-r-red' },
];
function reasonMeta(v) { return REASONS.find(r => r.value === v) || { value: v, label: v || 'unknown', cls: 'crmsp-r-slate' }; }

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function CrmSuppressions({ clientId }) {
  const { getToken } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newEmail, setNewEmail] = useState('');
  const [newReason, setNewReason] = useState('manual');
  const [adding, setAdding] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
      if (query.trim()) params.set('q', query.trim());
      if (reasonFilter) params.set('reason', reasonFilter);
      const res = await fetch(`${API_BASE}/clients/${clientId}/suppressions?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [clientId, offset, query, reasonFilter, getToken]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); }, [query, reasonFilter]);

  const addSingle = async () => {
    const email = newEmail.trim();
    if (!email) { toast.error('Enter an email'); return; }
    setAdding(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/clients/${clientId}/suppressions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, reason: newReason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Failed (${res.status})`);
      if (data.added) { toast.success('Address suppressed'); setNewEmail(''); await load(); }
      else toast('That address is already suppressed');
    } catch (e) { toast.error(e.message); }
    finally { setAdding(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.email} from the suppression list? It will be contactable again.`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/suppressions/${row.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      toast.success('Removed');
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE, total);

  return (
    <div className="crmsp-root">
      <div className="crmsp-inner">
        <div className="crmsp-add">
          <input className="crmsp-input crmsp-add-email" value={newEmail} onChange={e => setNewEmail(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addSingle(); }} placeholder="Add an address to suppress…" />
          <select className="crmsp-input crmsp-add-reason" value={newReason} onChange={e => setNewReason(e.target.value)}>
            {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <button className="crmsp-btn crmsp-btn--primary" onClick={addSingle} disabled={adding}>{adding ? 'Adding…' : 'Add'}</button>
          <button className="crmsp-btn" onClick={() => setBulkOpen(true)}>Bulk add</button>
        </div>

        <div className="crmsp-filters">
          <input className="crmsp-input crmsp-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search addresses…" />
          <select className="crmsp-input crmsp-reasonfilter" value={reasonFilter} onChange={e => setReasonFilter(e.target.value)}>
            <option value="">All reasons</option>
            {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <span className="crmsp-count">{total} suppressed</span>
        </div>

        {error && <div className="crmsp-error">{error}</div>}

        {loading ? (
          <div className="crmsp-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="crmsp-empty">
            {total === 0 && !query && !reasonFilter ? 'No suppressed addresses. Unsubscribes and bounces land here automatically; you can also add addresses manually.' : 'No matches.'}
          </div>
        ) : (
          <div className="crmsp-table">
            <div className="crmsp-thead">
              <span className="crmsp-th crmsp-th-email">Email</span>
              <span className="crmsp-th crmsp-th-reason">Reason</span>
              <span className="crmsp-th crmsp-th-source">Source</span>
              <span className="crmsp-th crmsp-th-date">Added</span>
              <span className="crmsp-th crmsp-th-act" />
            </div>
            {rows.map(row => {
              const rm = reasonMeta(row.reason);
              return (
                <div className="crmsp-tr" key={row.id}>
                  <span className="crmsp-td crmsp-td-email">{row.email}</span>
                  <span className="crmsp-td crmsp-td-reason"><span className={'crmsp-pill ' + rm.cls}>{rm.label}</span></span>
                  <span className="crmsp-td crmsp-td-source">{row.source || '—'}</span>
                  <span className="crmsp-td crmsp-td-date">{fmtDate(row.created_at)}</span>
                  <span className="crmsp-td crmsp-td-act"><button className="crmsp-del" onClick={() => remove(row)}>Remove</button></span>
                </div>
              );
            })}
          </div>
        )}

        {total > PAGE && (
          <div className="crmsp-pager">
            <span className="crmsp-pager-info">{pageStart}–{pageEnd} of {total}</span>
            <div className="crmsp-pager-btns">
              <button className="crmsp-btn crmsp-btn--sm" onClick={() => setOffset(Math.max(0, offset - PAGE))} disabled={offset === 0}>Previous</button>
              <button className="crmsp-btn crmsp-btn--sm" onClick={() => setOffset(offset + PAGE)} disabled={pageEnd >= total}>Next</button>
            </div>
          </div>
        )}
      </div>

      {bulkOpen && <BulkSuppressModal clientId={clientId} onClose={() => setBulkOpen(false)} onDone={async () => { setBulkOpen(false); setOffset(0); await load(); }} />}
    </div>
  );
}

function BulkSuppressModal({ clientId, onClose, onDone }) {
  const { getToken } = useAuth();
  const [text, setText] = useState('');
  const [reason, setReason] = useState('manual');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const emails = text.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);

  const submit = async () => {
    if (!emails.length) { toast.error('Paste at least one address'); return; }
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/clients/${clientId}/suppressions/bulk`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ emails, reason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Failed (${res.status})`);
      setResult(data);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="crmsp-ovl" onClick={onClose}>
      <div className="crmsp-dialog" onClick={e => e.stopPropagation()}>
        <div className="crmsp-dialog-head">
          <h3>Bulk suppress</h3>
          <button className="crmsp-x" onClick={onClose}>×</button>
        </div>
        <div className="crmsp-dialog-body">
          {result ? (
            <div className="crmsp-result">
              <div className="crmsp-result-big">{result.added}</div>
              <div className="crmsp-result-sub">newly suppressed of {result.submitted} submitted{result.submitted - result.added > 0 ? ` (${result.submitted - result.added} already on the list)` : ''}.</div>
            </div>
          ) : (
            <>
              <label className="crmsp-lbl">Paste addresses (separated by spaces, commas, or new lines)</label>
              <textarea className="crmsp-input crmsp-bulk-ta" value={text} onChange={e => setText(e.target.value)} rows={9} placeholder={"a@example.com\nb@example.com\nc@example.com"} />
              <div className="crmsp-bulk-row">
                <span className="crmsp-bulk-count">{emails.length} address{emails.length === 1 ? '' : 'es'} detected</span>
                <select className="crmsp-input crmsp-bulk-reason" value={reason} onChange={e => setReason(e.target.value)}>
                  {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
        <div className="crmsp-dialog-foot">
          {result ? (
            <button className="crmsp-btn crmsp-btn--primary" onClick={onDone}>Done</button>
          ) : (
            <>
              <button className="crmsp-btn" onClick={onClose}>Cancel</button>
              <button className="crmsp-btn crmsp-btn--primary" onClick={submit} disabled={saving || emails.length === 0}>{saving ? 'Suppressing…' : `Suppress ${emails.length || ''}`}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
